"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildChartRows = buildChartRows;
exports.buildMetricTiles = buildMetricTiles;
exports.formatDateLabel = formatDateLabel;
exports.getChartMetricDefinitions = getChartMetricDefinitions;
exports.getMetricTarget = getMetricTarget;
exports.getReadingsWindow = getReadingsWindow;
exports.getTodayDateKey = getTodayDateKey;
exports.shiftDateKey = shiftDateKey;
var device_metrics_1 = require("../constants/device-metrics");
var facility_dashboard_1 = require("../constants/facility-dashboard");
var device_metrics_2 = require("./device-metrics");
function toDateKey(date) {
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, "0");
    var day = String(date.getDate()).padStart(2, "0");
    return "".concat(year, "-").concat(month, "-").concat(day);
}
/** Local midnight of the given day; the key is parsed field by field so it never shifts through UTC. */
function startOfDateKey(dateKey) {
    var _a = dateKey.split("-").map(Number), year = _a[0], month = _a[1], day = _a[2];
    return new Date(year, month - 1, day, 0, 0, 0, 0);
}
function getTodayDateKey(now) {
    if (now === void 0) { now = new Date(); }
    return toDateKey(now);
}
function shiftDateKey(dateKey, days) {
    var shifted = startOfDateKey(dateKey);
    shifted.setDate(shifted.getDate() + days);
    return toDateKey(shifted);
}
/** "Today", "Yesterday", or the weekday and date, so the tiles can name the period without a dropdown label. */
function formatDateLabel(dateKey, now) {
    if (now === void 0) { now = new Date(); }
    if (dateKey === toDateKey(now)) {
        return "Today";
    }
    if (dateKey === shiftDateKey(toDateKey(now), -1)) {
        return "Yesterday";
    }
    return startOfDateKey(dateKey).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}
/** The whole local day, capped at now when the day is today so the window never asks for the future. */
function getReadingsWindow(dateKey, now) {
    if (now === void 0) { now = new Date(); }
    var from = startOfDateKey(dateKey);
    var endOfDay = startOfDateKey(shiftDateKey(dateKey, 1));
    var to = endOfDay > now ? now : endOfDay;
    return { from: from.toISOString(), to: to.toISOString() };
}
function fahrenheitToCelsius(value) {
    return ((value - 32) * 5) / 9;
}
/** Area targets are stored in °F when the area's metric flag is false; only temperature and humidity have targets. */
function getMetricTarget(area, metricKey) {
    var tolerance = facility_dashboard_1.TARGET_TOLERANCES[metricKey];
    if (!area || tolerance === undefined) {
        return undefined;
    }
    if (metricKey === "temperatureC" && typeof area.temperature === "number") {
        return { value: area.metric === false ? fahrenheitToCelsius(area.temperature) : area.temperature, tolerance: tolerance };
    }
    if (metricKey === "humidityPct" && typeof area.humidity === "number") {
        return { value: area.humidity, tolerance: tolerance };
    }
    return undefined;
}
/** Words first: the chip says how far and which way, and only then is it coloured. */
function getMetricStatus(metricSummary, target, definition) {
    if (metricSummary.current === null) {
        return { tone: "neutral", label: "No readings", icon: "solar:minus-circle-linear" };
    }
    if (!target) {
        return { tone: "neutral", label: "No target", icon: "solar:target-linear" };
    }
    var drift = metricSummary.current - target.value;
    var distance = Math.abs(drift);
    if (distance <= target.tolerance) {
        return { tone: "success", label: "In range", icon: "solar:check-circle-linear" };
    }
    return {
        tone: distance <= target.tolerance * 2 ? "warning" : "critical",
        label: "".concat((0, device_metrics_2.formatMetricValue)(distance, definition), " ").concat(drift > 0 ? "high" : "low"),
        icon: drift > 0 ? "solar:arrow-up-linear" : "solar:arrow-down-linear",
    };
}
/** The area-wide average per bucket, thinned to a handful of points so the tile stays a glance, not a chart. */
function buildMetricSparkline(series, metricKey) {
    var _a;
    var SPARKLINE_POINTS = 24;
    var sumsByTime = new Map();
    for (var _i = 0, series_1 = series; _i < series_1.length; _i++) {
        var entry = series_1[_i];
        if (entry.metric !== metricKey) {
            continue;
        }
        for (var _b = 0, _c = entry.points; _b < _c.length; _b++) {
            var point = _c[_b];
            var bucket = (_a = sumsByTime.get(point.time)) !== null && _a !== void 0 ? _a : { sum: 0, count: 0 };
            sumsByTime.set(point.time, { sum: bucket.sum + point.avg, count: bucket.count + 1 });
        }
    }
    var averages = Array.from(sumsByTime.entries())
        .sort(function (_a, _b) {
        var left = _a[0];
        var right = _b[0];
        return Date.parse(left) - Date.parse(right);
    })
        .map(function (_a) {
        var bucket = _a[1];
        return bucket.sum / bucket.count;
    });
    if (averages.length <= SPARKLINE_POINTS) {
        return averages;
    }
    var step = averages.length / SPARKLINE_POINTS;
    return Array.from({ length: SPARKLINE_POINTS }, function (_, index) { return averages[Math.min(averages.length - 1, Math.floor(index * step))]; });
}
function buildMetricTile(summary, definition, dateLabel, selectedArea) {
    var metricSummary = summary.summary.find(function (entry) { return entry.metric === definition.key; });
    if (!metricSummary) {
        return undefined;
    }
    var format = function (value) { return (0, device_metrics_2.formatMetricValue)(value, definition); };
    var target = getMetricTarget(selectedArea, definition.key);
    var hasLightSplit = metricSummary.lightsOn !== null || metricSummary.lightsOff !== null;
    var stats = hasLightSplit
        ? [
            { label: "Average", value: format(metricSummary.average) },
            { label: "Day", value: format(metricSummary.lightsOn) },
            { label: "Night", value: format(metricSummary.lightsOff) },
        ]
        : [
            { label: "Average", value: format(metricSummary.average) },
            { label: "Low", value: format(metricSummary.min) },
            { label: "High", value: format(metricSummary.max) },
        ];
    var footer = __spreadArray(__spreadArray(__spreadArray([], (target ? [{ label: "Target", value: format(target.value) }] : []), true), (hasLightSplit ? [{ label: "Range", value: "".concat(format(metricSummary.min), " to ").concat(format(metricSummary.max)) }] : []), true), [
        { label: "Day", value: dateLabel },
    ], false);
    return {
        key: definition.key,
        label: definition.label,
        icon: definition.icon,
        value: metricSummary.current === null ? "—" : metricSummary.current.toFixed(definition.digits),
        unit: definition.unit,
        status: getMetricStatus(metricSummary, target, definition),
        sparkline: buildMetricSparkline(summary.series, definition.key),
        stats: stats,
        footer: footer,
    };
}
function buildMetricTiles(summary, dateLabel, selectedArea) {
    return facility_dashboard_1.SUMMARY_METRIC_KEYS.map(function (key) {
        var definition = (0, device_metrics_2.getMetricDefinition)(key);
        return definition ? buildMetricTile(summary, definition, dateLabel, selectedArea) : undefined;
    }).filter(function (tile) { return tile !== undefined; });
}
function formatBucketTime(epochMs) {
    return new Date(epochMs).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
/** One row per bucket time with a column per device id, so Recharts can draw one line per device. */
function buildChartRows(series) {
    var _a;
    var rowsByEpoch = new Map();
    for (var _i = 0, series_2 = series; _i < series_2.length; _i++) {
        var entry = series_2[_i];
        for (var _b = 0, _c = entry.points; _b < _c.length; _b++) {
            var point = _c[_b];
            var epochMs = Date.parse(point.time);
            var row = (_a = rowsByEpoch.get(epochMs)) !== null && _a !== void 0 ? _a : {};
            row[entry.deviceId] = point.avg;
            rowsByEpoch.set(epochMs, row);
        }
    }
    return Array.from(rowsByEpoch.entries())
        .sort(function (_a, _b) {
        var left = _a[0];
        var right = _b[0];
        return left - right;
    })
        .map(function (_a) {
        var epochMs = _a[0], values = _a[1];
        return (__assign(__assign({}, values), { time: formatBucketTime(epochMs) }));
    });
}
/** Definitions for the metrics present in the response, in catalogue order so temperature and humidity come first. */
function getChartMetricDefinitions(summary) {
    var present = new Set(summary.series.map(function (entry) { return entry.metric; }));
    return Object.values(device_metrics_1.ALL_DEVICE_METRICS).filter(function (definition) { return present.has(definition.key); });
}
